import React from "react";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Button, Grid, TextField, Typography, Box } from "@mui/material";
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { baseFetch } from "../../helpers/apiClient";
import { formatPrice, discountedEventPrice } from "../../helpers/currency";
import { styled } from "@mui/system";
import "../../App.css";
import { toggleSessionModal } from "../../redux/sessionSlice";
import BillingProvinceSelect from "../../components/BillingProvinceSelect";

const PurchaseModal = ({ success, event, open, onClose, handleLoading }) => {
  const elements = useElements();
  const stripe = useStripe();
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [billingDetails, setBillingDetails] = useState({
    name: "",
    email: "",
    phone: "",
    line1: "",
    city: "",
    state: "",
    postal_code: "",
    country: "CA",
  });
  const [taxInfo, setTaxInfo] = useState(null);
  const [values, setValues] = useState({
    cardholderName: "",
    cardNumber: false,
    cvv: false,
    expiry: false,
  });
  const [subsError, setSubsError] = useState("");
  // A videos-and-presentations item can be backed by a video or a PDF; the PDF
  // case is labelled "Presentation" rather than "Video".
  const urlExtension = event?.resourceFile
    ? event.resourceFile.split("?")[0].split(".").pop().toLowerCase()
    : "";
  const isPdfPresentation =
    event?.eventType === "videos-and-presentations" &&
    (event?.resourceFileType?.toLowerCase().includes("pdf") ||
      urlExtension === "pdf");
  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const cardNumberElement = elements?.getElement(CardNumberElement);
    const cvvElement = elements?.getElement(CardCvcElement);
    const expiryElement = elements?.getElement(CardExpiryElement);

    const handleElementChange = (element, field) => {
      subsError && setSubsError("");
      if (element?.complete) {
        setValues((prevFields) => ({
          ...prevFields,
          [field]: true,
        }));
      } else {
        setValues((prevFields) => ({
          ...prevFields,
          [field]: false,
        }));
      }
    };

    cardNumberElement?.on("change", (event) => {
      handleElementChange(event, "cardNumber");
    });

    cvvElement?.on("change", (event) => {
      handleElementChange(event, "cvv");
    });

    expiryElement?.on("change", (event) => {
      handleElementChange(event, "expiry");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  const fetchTaxInfo = async () => {
    const payload = {
      purchaseType: "event",
      eventId: event?.id ?? event?._id,
      address: {
        line1: billingDetails?.line1,
        line2: "",
        city: billingDetails?.city,
        state: billingDetails?.state,
        country: billingDetails?.country,
        postal_code: billingDetails?.postal_code,
      },
    };
    try {
      const resp = await baseFetch({
        url: "/v1/web/subscription/tax-preview",
        method: "POST",
        body: payload,
        dispatch,
      });
      setTaxInfo(resp?.data);
      setSubsError("");
      setStep(2);
    } catch (errMsg) {
      setSubsError(errMsg);
    }
  };

  const handleChange = (name, value) => {
    subsError && setSubsError("");
    if (name === "cardholderName") {
      // Check if the input value contains only letters and spaces
      if (/^[A-Za-z\s]+$/.test(value) || value === "") {
        setValues((prevFields) => ({
          ...prevFields,
          [name]: value,
        }));
      } else {
        setValues((prevFields) => ({
          ...prevFields,
          [name]: "",
        }));
      }
    } else if (value.length > 20) {
      // Prevent updating the state if the length exceeds 20 characters
      return;
    } else {
      setValues((prevFields) => ({
        ...prevFields,
        [name]: value,
      }));
    }
  };

  const createOptions = () => {
    return {
      style: {
        base: {
          fontSize: "20px",
          fontWeight: "400",
          color: "#2A4876",
          letterSpacing: "0.025em",
          backgroundColor: "#F1F7FA",
          border: "2px solid red",
          "::placeholder": {
            color: "#D4D4D4",
            fontSize: "18px",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    };
  };

  const handleBlur = () => {
    console.log("[blur]");
  };

  const handleFocus = () => {
    console.log("[focus]");
  };
  const handleReady = () => {
    console.log("[ready]");
  };

  const handleSubmit = async () => {
    handleLoading(true);

    let paymentMethod = null;
    const { postal_code, city, state, country, line1 } = billingDetails;

    if (totalAmount !== 0) {
      if (!stripe || !elements) {
        return;
      }
      const { paymentMethod: pm, error } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: {
          address: {
            country,
            city,
            state,
            line1,
            postal_code,
          },
        },
      });
      if (error) {
        setSubsError(error.message);
        console.log("error !!", error);
        handleLoading(false);
        return;
      }
      paymentMethod = pm;
    }

    const payload = {
      paymentMethodId: paymentMethod?.id,
      eventId: event?.id ?? event?._id,
    };

    baseFetch({
      url: "/v1/web/event/purchase",
      method: "POST",
      body: payload,
    })
      .then((response) => {
        if (response.status) {
          success();
        }
      })
      .catch((err) => {
        console.log(err);
        setSubsError(err);
        handleLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };

  const isDisable =
    !values.cardholderName ||
    !values.cardNumber ||
    !values.cvv ||
    !values.expiry ||
    subsError
      ? true
      : false;

  const handleBillingNext = () => {
    if (
      !billingDetails.name ||
      !billingDetails.email ||
      !billingDetails.line1 ||
      !billingDetails.city ||
      !billingDetails.state ||
      !billingDetails.postal_code
    ) {
      setSubsError("Please fill all required billing fields");
      return;
    }
    fetchTaxInfo();
  };

  const taxAmount = taxInfo?.taxAmount ? taxInfo.taxAmount / 100 : 0;
  // Discounted fee from the shared helper (prefers backend integer-cents
  // finalAmount; clamps ≥ 0) so the modal never subtracts dollar floats.
  const finalFee = discountedEventPrice(event);
  const totalAmount = finalFee + taxAmount;

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff", // Background color of the dialog
          padding: {
            xs: 0,
            sm: "30px 40px",
            md: "30px 40px",
            lg: "30px 50px",
            xl: "40px 70px",
          },
          borderRadius: "20px", // Rounded corners
          maxWidth: {
            xs: "320px",
            sm: "550px",
            md: "550px",
            lg: "600px",
            xl: "640px",
          }, // Max width
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.7)", // Darker overlay
        },
      }}
    >
      <DialogContent>
        {step === 1 ? (
          <Box
            sx={{
              padding: "15px",
              height: "calc(100vh - 120px)",
              overflow: "auto",
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#2A4876",
                mb: 2,
              }}
            >
              Billing Address
            </Typography>

            <TextField
              fullWidth
              label="Full Name"
              value={billingDetails.name}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, name: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Email"
              value={billingDetails.email}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, email: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Phone"
              value={billingDetails.phone}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, phone: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Address Line 1"
              value={billingDetails.line1}
              onChange={(e) =>
                setBillingDetails({ ...billingDetails, line1: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="City"
                value={billingDetails.city}
                onChange={(e) =>
                  setBillingDetails({ ...billingDetails, city: e.target.value })
                }
              />

              <BillingProvinceSelect
                value={billingDetails.state}
                onChange={(e) =>
                  setBillingDetails({
                    ...billingDetails,
                    state: e.target.value,
                  })
                }
              />
            </Box>

            <Box display="flex" gap={2} mt={2}>
              <TextField
                fullWidth
                label="Postal Code"
                value={billingDetails.postal_code}
                onChange={(e) =>
                  setBillingDetails({
                    ...billingDetails,
                    postal_code: e.target.value,
                  })
                }
              />

              <TextField
                fullWidth
                label="Country Code"
                value={billingDetails.country}
                onChange={(e) =>
                  setBillingDetails({
                    ...billingDetails,
                    country: e.target.value,
                  })
                }
              />
            </Box>

            {subsError && (
              <Typography color="error" mt={2}>
                {subsError}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleBillingNext}
              sx={{ height: "60px", mt: 3, backgroundColor: "#2A4876" }}
            >
              Next
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography
              sx={{
                color: "#2A4876",
                textAlign: "center",
                fontSize: {
                  xs: "20px",
                  sm: "24px",
                  md: "24px",
                  lg: "26px",
                  xl: "32px",
                },
                fontWeight: 600,
              }}
            >
              {event?.eventType === "webinar"
                ? "Webinar"
                : event?.eventType === "videos-and-presentations"
                  ? isPdfPresentation
                    ? "Presentation"
                    : "Video"
                  : event?.eventType === "workshop"
                    ? "Workshop"
                    : "Event"}{" "}
              Amount
            </Typography>

            <div>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    marginTop: "15px",
                    marginBottom: "15px",
                    color: "#2A4876",
                    fontSize: "22px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    Reservation Fee
                  </Typography>
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    ${formatPrice(finalFee, true)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    marginTop: "15px",
                    marginBottom: "15px",
                    color: "#2A4876",
                    fontSize: "22px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    Tax
                  </Typography>
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    ${formatPrice(taxAmount, true)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    marginTop: "15px",
                    marginBottom: "15px",
                    color: "#2A4876",
                    fontSize: "22px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    Total Amount
                  </Typography>
                  <Typography
                    className="text-text-color text-xss"
                    variant="body"
                    sx={{ fontWeight: 600 }}
                  >
                    ${formatPrice(totalAmount, true)}
                  </Typography>
                </Box>
                <Typography
                  variant="body"
                  display="inline-block"
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    color: "#152134",
                    paddingBottom: "20px",
                  }}
                >
                  Your payment details are protected with the highest security
                  standards.
                </Typography>

                {totalAmount !== 0 && (
                  <>
                    <Box sx={{ marginTop: "15px" }}>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          color: "#152134",
                          textAlign: "left",
                          marginBottom: "5px",
                        }}
                      >
                        Enter Card Holder&apos;s Name
                      </Typography>
                      <RoundedTextField
                        name="cardholderName"
                        variant="outlined"
                        placeholder="Enter Name on Card"
                        fullWidth
                        type="text"
                        value={values?.cardholderName}
                        onChange={(e) =>
                          handleChange("cardholderName", e.target.value)
                        }
                      />
                    </Box>

                    <Box sx={{ marginTop: "10px" }}>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px",
                          color: "#152134",
                          textAlign: "left",
                          marginBottom: "5px",
                        }}
                      >
                        Enter Card Number
                      </Typography>
                      <CardNumberElement
                        className="input-box"
                        onBlur={handleBlur}
                        onChange={(event) =>
                          handleChange(
                            "cardNumber",
                            event.error || !event?.complete ? "" : "filled",
                          )
                        }
                        onFocus={handleFocus}
                        onReady={handleReady}
                        options={{
                          ...createOptions(),
                          placeholder: "Enter here",
                        }}
                      />
                    </Box>

                    <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                      <Grid item xs={12} lg={6}>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#152134",
                            textAlign: "left",
                            marginBottom: "5px",
                          }}
                        >
                          Enter Expiry Date
                        </Typography>
                        <div className="input-box">
                          <CardExpiryElement
                            onBlur={handleBlur}
                            onChange={(event) =>
                              handleChange(
                                "expiry",
                                event.error || !event?.complete ? "" : "filled",
                              )
                            }
                            onFocus={handleFocus}
                            onReady={handleReady}
                            options={createOptions()}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#152134",
                            textAlign: "left",
                            marginBottom: "5px",
                          }}
                        >
                          Enter CVV
                        </Typography>
                        <CardCvcElement
                          className="input-box"
                          onBlur={handleBlur}
                          onChange={(event) =>
                            handleChange(
                              "cvv",
                              event.error || !event?.complete ? "" : "filled",
                            )
                          }
                          onFocus={handleFocus}
                          onReady={handleReady}
                          options={{
                            ...createOptions(),
                            placeholder: "Enter here",
                          }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {subsError && (
                  <Typography
                    sx={{
                      color: "red",
                      marginTop: "15px",
                      marginBottom: "0px",
                    }}
                  >
                    {subsError}
                  </Typography>
                )}
              </Box>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isDisable && totalAmount !== 0}
              sx={{
                backgroundColor:
                  isDisable && totalAmount !== 0 ? "#CCC" : "#2A4876",
                width: "100%",
                marginTop: "20px",
                borderRadius: "50px",
                height: {
                  xs: "40px",
                  sm: "40px",
                  md: "40px",
                  lg: "50px",
                  xl: "70px",
                },
                fontSize: {
                  xs: "14px",
                  sm: "15px",
                  md: "16px",
                  lg: "16px",
                  xl: "18px",
                },
                textTransform: "capitalize",
                fontWeight: 600,
                color: isDisable && totalAmount !== 0 ? "#ccc" : "#fff",
              }}
            >
              {totalAmount === 0 ? "Register" : "Pay"}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
export default PurchaseModal;
const RoundedTextField = styled(TextField)({
  borderRadius: "50px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "50px",
    backgroundColor: "#F1F7FA", // Set the input background color
  },
  "& .MuiInputBase-input": {
    padding: {
      xs: "10px 15px",
      sm: "10px 15px",
      md: "10px 15px",
      lg: "12px 15px",
      xl: "15px",
    },
    color: "#2A4876", // Change text color for readability
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: make the border transparent
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent on hover
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent", // Optional: ensure border stays transparent when focused
  },
});
